import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { PostShiftReviewFlow } from "@src/appV2/PostShiftReview/PostShiftReviewFlow";
import { EmergencyCreditsEducationContainer } from "@src/appV2/redesign/ClipboardScore/components/EmergencyCreditsEducation/EmergencyCreditsEducationContainer";
import { useEmergencyCreditsEducation } from "@src/appV2/redesign/ClipboardScore/hooks/useEmergencyCreditsEducation";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { MY_SHIFTS_FULL_PATH } from "../../../path";
import { YourEmergencyCreditsPage } from "./YourEmergencyCredits/YourEmergencyCreditsPage";

interface RouteParams {
  shiftId: string;
}

interface LocationState {
  facility?: {
    id: string;
    name: string;
    type: string;
  };
  isDelayedWorkplaceReviewEnabled?: boolean;
  backgroundGradient?: string;
}

export function EmergencyCreditsClockOutFlowPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const postShiftReviewModalState = useModalState();
  const emergencyCreditsEducationModalState = useModalState();

  const { userId: workerId } = useDefinedWorker();
  const { facility, isDelayedWorkplaceReviewEnabled, backgroundGradient } = location.state ?? {};

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);
  const { emergencyCreditStats } = clipboardScoreProfile ?? {};

  const {
    hasSeenEmergencyCreditsEducation,
    markEmergencyCreditsEducationAsSeen,
    emergencyCreditsConfig,
  } = useEmergencyCreditsEducation();

  const handleContinue = useCallback((): void => {
    if ((isDelayedWorkplaceReviewEnabled ?? false) || !facility) {
      history.replace(MY_SHIFTS_FULL_PATH);
    } else {
      postShiftReviewModalState.openModal();
    }
  }, [history, isDelayedWorkplaceReviewEnabled, facility, postShiftReviewModalState]);

  const handlePostShiftReviewComplete = useCallback((): void => {
    postShiftReviewModalState.closeModal();
    history.replace(MY_SHIFTS_FULL_PATH);
  }, [history, postShiftReviewModalState]);

  const handleLearnMoreClick = useCallback((): void => {
    emergencyCreditsEducationModalState.openModal();
  }, [emergencyCreditsEducationModalState]);

  // Get credit stats or default values
  const cancellationStats = emergencyCreditStats?.cancellation ?? {
    completedShiftsTowardsNextCredit: 0,
    requiredShiftsForNextCredit: 20,
    totalAvailableCredits: 0,
  };

  const latenessStats = emergencyCreditStats?.lateness ?? {
    completedShiftsTowardsNextCredit: 0,
    requiredShiftsForNextCredit: 20,
    totalAvailableCredits: 0,
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <YourEmergencyCreditsPage
        backgroundGradient={backgroundGradient}
        cancellationStats={cancellationStats}
        latenessStats={latenessStats}
        onContinue={handleContinue}
        onLearnMoreClick={handleLearnMoreClick}
      />

      {facility && (
        <PostShiftReviewFlow
          modalState={postShiftReviewModalState}
          facility={{
            id: facility.id,
            name: facility.name,
            type: facility.type,
            photo: undefined,
          }}
          shiftId={shiftId}
          onComplete={handlePostShiftReviewComplete}
        />
      )}

      {isDefined(emergencyCreditsConfig) && (
        <EmergencyCreditsEducationContainer
          modalState={emergencyCreditsEducationModalState}
          markAsSeen={markEmergencyCreditsEducationAsSeen}
          showCloseButton={hasSeenEmergencyCreditsEducation}
          emergencyCreditsConfig={emergencyCreditsConfig}
        />
      )}
    </ThemeProvider>
  );
}
